import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { severityFromScore, SEV_STYLES } from "@/lib/severity";
import { SeverityBadge } from "@/components/Badges";
import { Shield, Target as TargetIcon, Sparkles, TrendingDown, DollarSign, ListChecks, Pencil } from "lucide-react";

const CATEGORIES = ["Cybersecurity", "Compliance", "Operations", "Vendor", "Financial", "People", "Physical", "Reputational"];
const SEV_HEX = { Critical: "#E11D48", High: "#EA580C", Moderate: "#F59E0B", Low: "#10B981" };

/** Compact 5×5 grid picker — click any cell to set likelihood/impact in one
 * gesture. Reads *so* much faster than two separate number inputs. */
function Grid5x5({ likelihood, impact, onPick, accent = "slate", testid }) {
  const accentRing = { rose: "focus-within:ring-rose-300", cyan: "focus-within:ring-cyan-300", emerald: "focus-within:ring-emerald-300" }[accent] || "focus-within:ring-slate-300";
  return (
    <div className={`inline-grid grid-cols-5 gap-0.5 rounded-md ${accentRing} focus-within:ring-2`} data-testid={testid}>
      {Array.from({ length: 5 }).map((_, r) =>
        Array.from({ length: 5 }).map((_, c) => {
          const l = c + 1, i = 5 - r;
          const score = l * i;
          const sev = severityFromScore(score);
          const isActive = l === likelihood && i === impact;
          return (
            <button
              key={`${l}-${i}`}
              type="button"
              onClick={() => onPick(l, i)}
              aria-label={`Likelihood ${l}, Impact ${i}, score ${score}`}
              className={`h-5 w-5 rounded-sm transition-all ${isActive ? "ring-2 ring-slate-900 scale-110" : "hover:scale-105 hover:opacity-80"}`}
              style={{ background: SEV_HEX[sev], opacity: isActive ? 1 : 0.26 }}
              title={`L${l} · I${i} · ${score} · ${sev}`}
            />
          );
        })
      )}
    </div>
  );
}

/** One assessment panel: title, L/I inputs AND click-to-pick 5x5 grid,
 * live severity badge, live score. Accent ties card to the stage.
 */
function AssessmentPanel({ kicker, label, icon: Icon, likelihood, impact, setLikelihood, setImpact, accent, testIdL, testIdI, testIdGrid }) {
  const score = (likelihood || 0) * (impact || 0);
  const sev = severityFromScore(score);
  const s = SEV_STYLES[sev];
  const cardCls = accent === "rose"    ? "border-rose-200 bg-rose-50/40"
               : accent === "cyan"    ? "border-cyan-300 bg-cyan-50/40 ring-1 ring-cyan-200/70"
               : accent === "emerald" ? "border-emerald-200 bg-emerald-50/40"
               : "border-slate-200 bg-slate-50/40";
  const kickerCls = accent === "rose"    ? "text-rose-700"
                 : accent === "cyan"    ? "text-cyan-800"
                 : accent === "emerald" ? "text-emerald-800"
                 : "text-slate-500";
  const clamp = (v) => Math.min(5, Math.max(1, Number(v) || 1));
  return (
    <div className={`relative rounded-xl border p-3.5 ${cardCls}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className={`h-3.5 w-3.5 ${kickerCls}`} />}
          <div className={`text-[10px] uppercase tracking-widest font-mono font-semibold ${kickerCls}`}>{kicker}</div>
        </div>
        <SeverityBadge severity={sev} />
      </div>
      <div className="text-[12px] font-semibold text-slate-900 mb-3">{label}</div>

      {/* Row 1: 5×5 grid picker on the left, big score on the right */}
      <div className="flex items-center gap-3">
        <Grid5x5 likelihood={likelihood} impact={impact} onPick={(l, i) => { setLikelihood(l); setImpact(i); }} accent={accent} testid={testIdGrid} />
        <div className="ml-auto text-right">
          <div className="text-[9px] uppercase tracking-widest font-mono text-slate-400">Score</div>
          <div className="font-mono font-extrabold text-[32px] tabular-nums leading-none mt-0.5" style={{ color: s.hex }}>{score}</div>
        </div>
      </div>

      {/* Row 2: Likelihood / Impact inputs underneath — no more overlap */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Likelihood</Label>
          <Input type="number" min={1} max={5} value={likelihood} onChange={(e) => setLikelihood(clamp(e.target.value))} className="h-8 text-sm" data-testid={testIdL} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Impact</Label>
          <Input type="number" min={1} max={5} value={impact} onChange={(e) => setImpact(clamp(e.target.value))} className="h-8 text-sm" data-testid={testIdI} />
        </div>
      </div>
    </div>
  );
}

export default function RiskFormDialog({ registers, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [title, setTitle] = useState(existing?.title || "");
  const [statement, setStatement] = useState(existing?.statement || "");
  const [category, setCategory] = useState(existing?.category || "Cybersecurity");
  const [registerId, setRegisterId] = useState(existing?.register_id || registers?.[0]?.id || "");
  const [ownerName, setOwnerName] = useState(existing?.owner_name || "");
  const [il, setIl] = useState(existing?.inherent?.likelihood ?? 3);
  const [ii, setIi] = useState(existing?.inherent?.impact ?? 3);
  const [cl, setCl] = useState(existing?.current?.likelihood ?? 2);
  const [ci, setCi] = useState(existing?.current?.impact ?? 3);
  // Target now explicitly editable. Default to one step below current on each axis.
  const [tl, setTl] = useState(existing?.target?.likelihood ?? Math.max(1, (existing?.current?.likelihood ?? 2) - 1));
  const [ti, setTi] = useState(existing?.target?.impact     ?? Math.max(1, (existing?.current?.impact     ?? 3) - 1));
  const [plan, setPlan] = useState(existing?.treatment_plan || "");
  const [financialImpact, setFinancialImpact] = useState(existing?.financial_impact ?? "");
  const [status, setStatus] = useState(existing?.status || "Active");
  const [busy, setBusy] = useState(false);

  const inhScore = il * ii;
  const curScore = cl * ci;
  const tgtScore = tl * ti;
  const dropNow = inhScore > 0 ? Math.round(((inhScore - curScore) / inhScore) * 100) : 0;
  const dropPlanned = inhScore > 0 ? Math.round(((inhScore - tgtScore) / inhScore) * 100) : 0;

  const submit = async () => {
    if (!title || !statement || !registerId) { toast.error("Title, statement and register are required"); return; }
    setBusy(true);
    try {
      const payload = {
        title, statement, category, owner_name: ownerName || null,
        inherent: { likelihood: +il, impact: +ii, rationale: "Inherent · before controls" },
        current:  { likelihood: +cl, impact: +ci, rationale: "Current · with existing controls" },
        target:   { likelihood: +tl, impact: +ti, rationale: "Target after treatment" },
        treatment_plan: plan || null,
        financial_impact: financialImpact === "" ? null : Number(financialImpact),
      };
      if (isEdit) {
        await api.patch(`/risks/${existing.id}`, { ...payload, status, version: existing.version });
        toast.success("Risk updated");
      } else {
        await api.post(`/risks?register_id=${registerId}`, { ...payload, tags: [] });
        toast.success("Risk created");
      }
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="risk-form-dialog">
        {/* Hero band — matches detail page vibe so the edit screen feels like
            the same object, not a bland form. */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0A1329] to-[#111E42] text-white px-6 py-5">
          <div className="absolute -top-16 -right-10 w-[260px] h-[260px] rounded-full bg-cyan-500/10 blur-2xl pointer-events-none" aria-hidden />
          <DialogHeader className="relative">
            <DialogTitle className="font-display font-bold text-[22px] text-white flex items-center gap-2 tracking-tight">
              <Pencil className="h-4 w-4 text-cyan-300" />
              {isEdit ? `Edit ${existing.code}` : "Create new risk"}
            </DialogTitle>
            <div className="text-[13px] text-slate-200 mt-1.5 leading-relaxed">
              Score the risk at three stages. Click any heatmap cell to set likelihood × impact.
            </div>
          </DialogHeader>
          {/* Live treatment summary */}
          {isEdit || inhScore > 0 ? (
            <div className="relative mt-4 grid grid-cols-3 gap-2.5">
              <LiveKpi label="Score today" value={curScore} sev={severityFromScore(curScore)} />
              <LiveKpi label="Drop with controls" value={`${dropNow}%`} tone="cyan" hint={`${inhScore} → ${curScore}`} />
              <LiveKpi label="Drop once treated" value={`${dropPlanned}%`} tone="emerald" hint={`${inhScore} → ${tgtScore}`} />
            </div>
          ) : null}
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Basics */}
          <section>
            <SectionHeader icon={ListChecks} kicker="Risk basics" title="What is it?" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="col-span-2">
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Ransomware compromise of production systems" data-testid="risk-title" />
              </div>
              <div className="col-span-2">
                <Label>Statement</Label>
                <Textarea rows={3} value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="Describe the risk, cause and consequence." data-testid="risk-statement" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="risk-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Register</Label>
                <Select value={registerId} onValueChange={setRegisterId}>
                  <SelectTrigger data-testid="risk-register"><SelectValue /></SelectTrigger>
                  <SelectContent>{registers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Owner (name)</Label>
                <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Owner name" data-testid="risk-owner" />
              </div>
              {isEdit && (
                <div className="col-span-2">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="risk-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Active", "Mitigated", "Accepted", "PendingAcceptance", "Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </section>

          {/* Scoring */}
          <section>
            <SectionHeader icon={TrendingDown} kicker="Scoring" title="Where does the risk sit?" hint="Three stages: before controls, with existing controls, after treatment." />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <AssessmentPanel kicker="Inherent" label="Before controls"        icon={Shield}     likelihood={il} impact={ii} setLikelihood={setIl} setImpact={setIi} accent="rose"    testIdL="risk-il" testIdI="risk-ii" testIdGrid="risk-grid-inherent" />
              <AssessmentPanel kicker="Current"  label="With existing controls" icon={Sparkles}   likelihood={cl} impact={ci} setLikelihood={setCl} setImpact={setCi} accent="cyan"    testIdL="risk-cl" testIdI="risk-ci" testIdGrid="risk-grid-current" />
              <AssessmentPanel kicker="Target"   label="After treatment"        icon={TargetIcon} likelihood={tl} impact={ti} setLikelihood={setTl} setImpact={setTi} accent="emerald" testIdL="risk-tl" testIdI="risk-ti" testIdGrid="risk-grid-target" />
            </div>
          </section>

          {/* Quant + plan */}
          <section>
            <SectionHeader icon={DollarSign} kicker="Exposure & plan" title="Money and mitigation" />
            <div className="grid grid-cols-1 gap-3 mt-3">
              <div>
                <Label>Financial impact / exposure <span className="text-slate-400 font-normal">(optional)</span></Label>
                <div className="relative">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  <Input type="number" value={financialImpact} onChange={(e) => setFinancialImpact(e.target.value)} placeholder="e.g. 250000. Breaches appetite when above the category cap." data-testid="risk-financial-impact" className="pl-7" />
                </div>
              </div>
              <div>
                <Label>Treatment plan <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea rows={3} value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="Deploy immutable backups, incident response retainer signed, …" data-testid="risk-plan" />
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-slate-50/30">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="risk-submit">
            {isEdit ? "Save changes" : "Create risk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionHeader({ icon: Icon, kicker, title, hint }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-8 w-8 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-cyan-700" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">{kicker}</div>
        <div className="font-display font-semibold text-slate-900">{title}</div>
        {hint && <div className="text-[11.5px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function LiveKpi({ label, value, hint, sev, tone }) {
  const color = sev ? SEV_HEX[sev] : tone === "cyan" ? "#67E8F9" : tone === "emerald" ? "#6EE7B7" : "#CBD5E1";
  return (
    <div className="rounded-lg bg-white/10 border border-white/20 backdrop-blur-sm px-3 py-2.5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-100 font-semibold">{label}</div>
      <div className="font-display font-extrabold text-[22px] tabular-nums leading-tight mt-1" style={{ color }}>{value}</div>
      {hint && <div className="text-[11px] font-mono text-slate-300 mt-0.5 tabular-nums">{hint}</div>}
    </div>
  );
}
